// ============================================================
// Chess Arena — Move Validator
// ============================================================

import { GridPos, PieceState, PieceType, BOARD_COLS, BOARD_ROWS } from './types';
import { Board } from './board';

// --- Helpers ---

function posEq(a: GridPos, b: GridPos): boolean {
  return a.col === b.col && a.row === b.row;
}

/** Check if a target cell is occupied by a friendly piece. */
function isFriendlyOccupied(pos: GridPos, team: string, board: Board, pieces: PieceState[]): boolean {
  const cell = board.getCellContent(pos);
  if (!cell || cell.pieceId === null) return false;
  const occupant = pieces.find((p) => p.id === cell.pieceId && p.isAlive);
  return occupant ? occupant.team === team : false;
}

/** Check if a target cell is occupied by an enemy piece. */
function isEnemyOccupied(pos: GridPos, team: string, board: Board, pieces: PieceState[]): boolean {
  const cell = board.getCellContent(pos);
  if (!cell || cell.pieceId === null) return false;
  const occupant = pieces.find((p) => p.id === cell.pieceId && p.isAlive);
  return occupant ? occupant.team !== team : false;
}

function isOccupied(pos: GridPos, board: Board): boolean {
  const cell = board.getCellContent(pos);
  return cell !== null && cell.pieceId !== null;
}

// --- Sliding moves (Bishop, Rook, Queen) ---

/** Maximum sliding distance for Bishop, Rook, Queen. */
const MAX_SLIDE_RANGE = 4;

function getSlidingMoves(
  origin: GridPos,
  directions: GridPos[],
  team: string,
  board: Board,
  pieces: PieceState[],
): GridPos[] {
  const moves: GridPos[] = [];
  for (const dir of directions) {
    let col = origin.col + dir.col;
    let row = origin.row + dir.row;
    let steps = 0;
    while (col >= 0 && col < BOARD_COLS && row >= 0 && row < BOARD_ROWS && steps < MAX_SLIDE_RANGE) {
      const pos: GridPos = { col, row };
      if (!board.isPassable(pos)) break; // rock blocks
      if (isFriendlyOccupied(pos, team, board, pieces)) break; // friendly blocks
      moves.push(pos);
      steps++;
      if (isEnemyOccupied(pos, team, board, pieces)) break; // capture then stop
      col += dir.col;
      row += dir.row;
    }
  }
  return moves;
}

// --- Per-type move generators ---

const ORTHOGONAL: GridPos[] = [
  { col: 0, row: -1 },
  { col: 0, row: 1 },
  { col: -1, row: 0 },
  { col: 1, row: 0 },
];

const DIAGONAL: GridPos[] = [
  { col: -1, row: -1 },
  { col: 1, row: -1 },
  { col: -1, row: 1 },
  { col: 1, row: 1 },
];

const ALL_DIRECTIONS: GridPos[] = [...ORTHOGONAL, ...DIAGONAL];

const KNIGHT_OFFSETS: GridPos[] = [
  { col: -2, row: -1 }, { col: -2, row: 1 },
  { col: 2, row: -1 },  { col: 2, row: 1 },
  { col: -1, row: -2 }, { col: 1, row: -2 },
  { col: -1, row: 2 },  { col: 1, row: 2 },
];

function getPawnMoves(piece: PieceState, board: Board, pieces: PieceState[]): GridPos[] {
  const moves: GridPos[] = [];
  const { col, row } = piece.pos;

  // Orthogonal movement (move only, no capture) — 1 square in any orthogonal direction
  for (const dir of ORTHOGONAL) {
    const target: GridPos = { col: col + dir.col, row: row + dir.row };
    if (!board.isValidPos(target)) continue;
    if (!board.isPassable(target)) continue;
    if (isOccupied(target, board)) continue; // blocked if occupied by any piece
    moves.push(target);
  }

  // Diagonal capture — 1 square diagonally, only if enemy present
  // Pawns CANNOT move diagonally into bush cells
  for (const dir of DIAGONAL) {
    const target: GridPos = { col: col + dir.col, row: row + dir.row };
    if (!board.isValidPos(target)) continue;
    if (!board.isPassable(target)) continue;
    if (board.isBush(target)) continue; // pawns cannot move diagonally into bushes
    if (isEnemyOccupied(target, piece.team, board, pieces)) {
      moves.push(target);
    }
  }

  return moves;
}

function getKnightMoves(piece: PieceState, board: Board, pieces: PieceState[]): GridPos[] {
  const moves: GridPos[] = [];
  const { col, row } = piece.pos;

  for (const off of KNIGHT_OFFSETS) {
    const target: GridPos = { col: col + off.col, row: row + off.row };
    if (!board.isValidPos(target)) continue;
    if (!board.isPassable(target)) continue; // can't land on rocks
    if (isFriendlyOccupied(target, piece.team, board, pieces)) continue;
    moves.push(target);
  }

  return moves;
}

function getBishopMoves(piece: PieceState, board: Board, pieces: PieceState[]): GridPos[] {
  return getSlidingMoves(piece.pos, DIAGONAL, piece.team, board, pieces);
}

function getRookMoves(piece: PieceState, board: Board, pieces: PieceState[]): GridPos[] {
  return getSlidingMoves(piece.pos, ORTHOGONAL, piece.team, board, pieces);
}

function getQueenMoves(piece: PieceState, board: Board, pieces: PieceState[]): GridPos[] {
  return getSlidingMoves(piece.pos, ALL_DIRECTIONS, piece.team, board, pieces);
}

function getKingMoves(piece: PieceState, board: Board, pieces: PieceState[]): GridPos[] {
  const moves: GridPos[] = [];
  const { col, row } = piece.pos;

  for (const dir of ALL_DIRECTIONS) {
    const target: GridPos = { col: col + dir.col, row: row + dir.row };
    if (!board.isValidPos(target)) continue;
    if (!board.isPassable(target)) continue;
    if (isFriendlyOccupied(target, piece.team, board, pieces)) continue;
    moves.push(target);
  }

  return moves;
}

// --- Move generators by type ---

const moveGenerators: Record<
  PieceType,
  (piece: PieceState, board: Board, pieces: PieceState[]) => GridPos[]
> = {
  Pawn: getPawnMoves,
  Knight: getKnightMoves,
  Bishop: getBishopMoves,
  Rook: getRookMoves,
  Queen: getQueenMoves,
  King: getKingMoves,
};

// --- Public API ---

/**
 * Get all valid move targets for a piece.
 * @param piece  The piece state to evaluate.
 * @param board  The board instance for terrain / occupancy queries.
 * @param pieces All alive piece states (used to determine friendly / enemy occupation).
 * @returns Array of valid GridPos targets.
 */
export function getValidMoves(
  piece: PieceState,
  board: Board,
  pieces: PieceState[],
): GridPos[] {
  const generator = moveGenerators[piece.type];
  if (!generator) return [];
  return generator(piece, board, pieces);
}
